// src/pages/SummernoteTestPage.tsx
// Test page for Summernote rich text editor component

import React, { useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import logoCorge from '../assets/logos/logo-corgee.png';
import ReactSummernoteCustom, { type SummernoteAPI } from '../components/Globals/SummernoteCustom';
import '../components/Globals/ConversationSummernoteStyles.css';

const SummernoteTestPage: React.FC = () =>
{
  // --- Basic Editor ---
  const basicRef = useRef<SummernoteAPI>(null);
  const [basicContent, setBasicContent] = useState<string>(
    '<p>Hello <b>Summernote</b>! Start typing here...</p>',
  );

  // --- Full Toolbar Editor ---
  const fullRef = useRef<SummernoteAPI>(null);
  const [fullContent, setFullContent] = useState<string>('');

  // --- Controlled Editor ---
  const controlledRef = useRef<SummernoteAPI>(null);
  const [controlledContent, setControlledContent] = useState<string>(
    '<p>This is a <i>controlled</i> editor.</p>',
  );

  // --- Disabled Editor ---
  const [isDisabled, setIsDisabled] = useState<boolean>(true);

  // --- Read-only Preview ---
  const [previewHtml, setPreviewHtml] = useState<string>('');

  // --- Event Log ---
  const [eventLog, setEventLog] = useState<string[]>([]);
  const addLog = useCallback((msg: string) =>
  {
    setEventLog(prev => [`[${new Date().toLocaleTimeString()}] ${msg}`, ...prev].slice(0, 30));
  }, []);

  // --- Image upload handler ---
  const handleImageUpload = useCallback(
    (images: File[], insertImage: (url: string, filename?: string) => void) =>
    {
      addLog(`Image upload: ${images.length} file(s)`);
      Array.from(images).forEach(file =>
      {
        const reader = new FileReader();
        reader.onload = e =>
        {
          if (e.target?.result)
          {
            insertImage(e.target.result as string, file.name);
            addLog(`Inserted image: ${file.name}`);
          }
        };
        reader.readAsDataURL(file);
      });
    },
    [addLog],
  );

  return (
    <div className="min-h-screen bg-[#ededed]">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={logoCorge} alt="Corgee Logo" className="w-10 h-10 object-contain" />
            <div>
              <h1 className="text-2xl font-semibold text-[#5dade2]">Summernote Test</h1>
              <p className="text-sm text-gray-500">Rich Text Editor Component</p>
            </div>
          </div>

          <Link to="/dashboard">
            <Button variant="outline">Volver al Dashboard</Button>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* 1. Basic Editor */}
        <section className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <span className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-bold">
              1
            </span>
            Basic Summernote Editor
          </h2>
          <p className="text-gray-600 mb-4">
            Default configuration with standard toolbar. Supports bold, italic, underline, fonts,
            colors, lists, links, and images.
          </p>

          <ReactSummernoteCustom
            ref={basicRef}
            value={basicContent}
            options={{
              height: 200,
              placeholder: 'Type something here...',
              toolbar: [
                ['style', ['bold', 'italic', 'underline', 'strikethrough']],
                ['fontname', ['fontname']],
                ['fontsize', ['fontsize']],
                ['color', ['color']],
                ['para', ['ul', 'ol', 'paragraph']],
                ['insert', ['link', 'picture', 'table', 'hr']],
                ['view', ['codeview', 'fullscreen']],
              ],
            }}
            onChange={content =>
            {
              setBasicContent(content);
              addLog('Basic editor: onChange');
            }}
            onFocus={() => addLog('Basic editor: onFocus')}
            onBlur={() => addLog('Basic editor: onBlur')}
            onImageUpload={handleImageUpload}
          />

          <div className="mt-4 flex gap-2">
            <Button variant="outline" onClick={() => basicRef.current?.focus()}>
              Focus
            </Button>
            <Button variant="outline" onClick={() => basicRef.current?.reset()}>
              Reset
            </Button>
            <Button
              variant="outline"
              onClick={() => alert(`isEmpty: ${basicRef.current?.isEmpty()}`)}
            >
              Check Empty
            </Button>
            <Button variant="outline" onClick={() => setPreviewHtml(basicContent)}>
              Preview HTML
            </Button>
          </div>
        </section>

        {/* 2. Full Toolbar Editor */}
        <section className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <span className="w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-sm font-bold">
              2
            </span>
            Full Toolbar Editor
          </h2>
          <p className="text-gray-600 mb-4">
            Extended toolbar with all Summernote options including style presets, tables, and code
            view.
          </p>

          <ReactSummernoteCustom
            ref={fullRef}
            options={{
              height: 300,
              placeholder: 'Full featured editor...',
              fontNames: [
                'Arial',
                'Arial Black',
                'Comic Sans MS',
                'Courier New',
                'Roboto',
                'Helvetica',
                'Georgia',
                'Times New Roman',
                'Verdana',
              ],
              toolbar: [
                ['style', ['style']],
                [
                  'font',
                  [
                    'bold',
                    'italic',
                    'underline',
                    'strikethrough',
                    'superscript',
                    'subscript',
                    'clear',
                  ],
                ],
                ['fontname', ['fontname']],
                ['fontsize', ['fontsize']],
                ['color', ['color']],
                ['para', ['ul', 'ol', 'paragraph', 'lineheight']],
                ['table', ['table']],
                ['insert', ['link', 'picture', 'video', 'hr']],
                ['view', ['fullscreen', 'codeview', 'help']],
              ],
            }}
            onChange={content =>
            {
              setFullContent(content);
              addLog('Full editor: onChange');
            }}
            onImageUpload={handleImageUpload}
          />

          <div className="mt-4 flex gap-2">
            <Button
              variant="outline"
              onClick={() => fullRef.current?.insertText('Inserted text! ')}
            >
              Insert Text
            </Button>
            <Button
              variant="outline"
              onClick={() =>
              {
                const node = document.createElement('span');
                node.style.color = '#62aacb';
                node.style.fontWeight = '600';
                node.textContent = '@MentionExample ';
                fullRef.current?.insertNode(node);
              }}
            >
              Insert Node (Mention)
            </Button>
            <Button variant="outline" onClick={() => setPreviewHtml(fullContent)}>
              Preview HTML
            </Button>
          </div>
        </section>

        {/* 3. Controlled Value Editor */}
        <section className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <span className="w-8 h-8 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-sm font-bold">
              3
            </span>
            Controlled Value Editor
          </h2>
          <p className="text-gray-600 mb-4">
            Demonstrates programmatic value changes via the{' '}
            <code className="bg-gray-100 px-1 rounded">value</code> prop and{' '}
            <code className="bg-gray-100 px-1 rounded">replace()</code> method.
          </p>

          <ReactSummernoteCustom
            ref={controlledRef}
            value={controlledContent}
            options={{
              height: 150,
              toolbar: [
                ['style', ['bold', 'italic', 'underline']],
                ['fontname', ['fontname']],
                ['color', ['color']],
                ['para', ['ul', 'ol']],
              ],
            }}
            onChange={content =>
            {
              setControlledContent(content);
              addLog('Controlled editor: onChange');
            }}
          />

          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              variant="default"
              onClick={() =>
                setControlledContent('<p><b>Bold content</b> set programmatically!</p>')
              }
            >
              Set Bold Content
            </Button>
            <Button
              variant="secondary"
              onClick={() =>
                setControlledContent('<ul><li>Item 1</li><li>Item 2</li><li>Item 3</li></ul>')
              }
            >
              Set List Content
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                setControlledContent('<p style="color: #62aacb;">Corgee blue text</p>')
              }
            >
              Set Colored Content
            </Button>
            <Button variant="destructive" onClick={() => setControlledContent('')}>
              Clear
            </Button>
          </div>
        </section>

        {/* 4. Disabled State Editor */}
        <section className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <span className="w-8 h-8 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center text-sm font-bold">
              4
            </span>
            Disabled / Read-only State
          </h2>
          <p className="text-gray-600 mb-4">
            Toggle the editor between enabled and disabled states.
          </p>

          <ReactSummernoteCustom
            value="<p>This editor can be <b>enabled</b> or <b>disabled</b>. Try toggling the button below.</p>"
            disabled={isDisabled}
            options={{
              height: 120,
              toolbar: [
                ['style', ['bold', 'italic', 'underline']],
                ['para', ['ul', 'ol']],
              ],
            }}
            onChange={() => addLog(`Disabled editor: onChange (disabled=${isDisabled})`)}
          />

          <div className="mt-4">
            <Button
              variant={isDisabled ? 'default' : 'destructive'}
              onClick={() => setIsDisabled(!isDisabled)}
            >
              {isDisabled ? 'Enable Editor' : 'Disable Editor'}
            </Button>
            <span className="ml-3 text-sm text-gray-500">
              State: <strong>{isDisabled ? 'Disabled' : 'Enabled'}</strong>
            </span>
          </div>
        </section>

        {/* 5. HTML Preview */}
        {previewHtml && (
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center text-sm font-bold">
                5
              </span>
              HTML Preview
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h3 className="font-medium text-gray-700 mb-2">Raw HTML</h3>
                <pre className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm overflow-auto max-h-[300px] whitespace-pre-wrap break-words">
                  {previewHtml}
                </pre>
              </div>
              <div>
                <h3 className="font-medium text-gray-700 mb-2">Rendered Output</h3>
                <div
                  className="bg-gray-50 border border-gray-200 rounded-lg p-4 prose prose-sm max-w-none max-h-[300px] overflow-auto"
                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                />
              </div>
            </div>

            <div className="mt-3">
              <Button variant="outline" onClick={() => setPreviewHtml('')}>
                Close Preview
              </Button>
            </div>
          </section>
        )}

        {/* 6. Event Log */}
        <section className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <span className="w-8 h-8 bg-red-100 text-red-600 rounded-full flex items-center justify-center text-sm font-bold">
              6
            </span>
            Event Log
          </h2>
          <p className="text-gray-600 mb-2">
            Captures onChange, onFocus, onBlur, and other events from the editors above.
          </p>

          <div className="bg-gray-900 text-green-400 rounded-lg p-4 font-mono text-xs max-h-[250px] overflow-auto">
            {eventLog.length === 0 ? (
              <span className="text-gray-500">
                No events yet. Interact with the editors above...
              </span>
            ) : (
              eventLog.map((log, i) => (
                <div key={i} className={i === 0 ? 'text-green-300' : 'text-green-600'}>
                  {log}
                </div>
              ))
            )}
          </div>

          <div className="mt-3">
            <Button variant="outline" onClick={() => setEventLog([])}>
              Clear Log
            </Button>
          </div>
        </section>

        {/* Summary */}
        <section className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl shadow-md p-6 border border-blue-200">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Summernote Integration Summary
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="font-medium text-gray-700 mb-2">Library</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>Summernote-lite 0.9.0 (no Bootstrap)</li>
                <li>jQuery 4.0 compatible</li>
                <li>Local vendor files (not npm)</li>
              </ul>
            </div>
            <div>
              <h3 className="font-medium text-gray-700 mb-2">Components</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>
                  <code className="bg-gray-100 px-1 rounded">ReactSummernoteCustom</code> -
                  Low-level wrapper
                </li>
                <li>
                  <code className="bg-gray-100 px-1 rounded">SummernoteCorgee</code> - Business
                  logic (mentions, links)
                </li>
                <li>
                  <code className="bg-gray-100 px-1 rounded">CustomFields/Summernote</code> - Custom
                  fields variant
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-medium text-gray-700 mb-2">API Methods</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>
                  <code className="bg-gray-100 px-1 rounded">focus()</code>,{' '}
                  <code className="bg-gray-100 px-1 rounded">reset()</code>,{' '}
                  <code className="bg-gray-100 px-1 rounded">isEmpty()</code>
                </li>
                <li>
                  <code className="bg-gray-100 px-1 rounded">replace(content)</code>,{' '}
                  <code className="bg-gray-100 px-1 rounded">enable()</code>,{' '}
                  <code className="bg-gray-100 px-1 rounded">disable()</code>
                </li>
                <li>
                  <code className="bg-gray-100 px-1 rounded">insertImage()</code>,{' '}
                  <code className="bg-gray-100 px-1 rounded">insertNode()</code>,{' '}
                  <code className="bg-gray-100 px-1 rounded">insertText()</code>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-medium text-gray-700 mb-2">Events</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>
                  <code className="bg-gray-100 px-1 rounded">onChange</code>,{' '}
                  <code className="bg-gray-100 px-1 rounded">onInit</code>
                </li>
                <li>
                  <code className="bg-gray-100 px-1 rounded">onFocus</code>,{' '}
                  <code className="bg-gray-100 px-1 rounded">onBlur</code>
                </li>
                <li>
                  <code className="bg-gray-100 px-1 rounded">onKeyUp</code>,{' '}
                  <code className="bg-gray-100 px-1 rounded">onKeyDown</code>,{' '}
                  <code className="bg-gray-100 px-1 rounded">onPaste</code>
                </li>
                <li>
                  <code className="bg-gray-100 px-1 rounded">onImageUpload</code>
                </li>
              </ul>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 text-center text-sm text-gray-500">
          Corgee - Summernote Test Page
        </div>
      </footer>
    </div>
  );
};

export default SummernoteTestPage;
